import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import type { PricingRuleResponse } from "@bw/shared/types/generated";
import { formatMoney } from "@bw/shared/utils";

import {
  formatFacilityService,
  formatRuleRepeat,
  formatRuleTime,
  type FacilityRead,
} from "./facilityDetailFormatters";

export function FacilityPricingPanel({
  pricing,
}: {
  pricing: FacilityRead<PricingRuleResponse[]>;
}) {
  const rules = pricing.data ?? [];

  return (
    <Paper
      component="section"
      aria-labelledby="facility-pricing-heading"
      variant="outlined"
      sx={{ minWidth: 0, overflow: "hidden" }}
    >
      <Typography
        id="facility-pricing-heading"
        component="h2"
        variant="h6"
        sx={{ fontWeight: 700, px: { xs: 2, md: 2.5 }, pt: { xs: 2, md: 2.5 } }}
      >
        Pricing rules
      </Typography>
      {pricing.state === "loading" ? (
        <Box
          role="status"
          aria-label="Loading pricing"
          aria-live="polite"
          sx={{ display: "flex", justifyContent: "center", py: 5 }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : pricing.state === "error" ? (
        <Alert
          severity="error"
          role="alert"
          action={
            <Button color="inherit" size="small" onClick={pricing.onRetry}>
              Retry
            </Button>
          }
          sx={{ m: { xs: 2, md: 2.5 } }}
        >
          Pricing rules could not be loaded. Try again.
        </Alert>
      ) : rules.length === 0 ? (
        <Typography
          color="text.secondary"
          variant="body2"
          sx={{ p: { xs: 2, md: 2.5 } }}
        >
          No pricing rules configured.
        </Typography>
      ) : (
        <TableContainer
          aria-label="Facility pricing rules"
          tabIndex={0}
          sx={{ mt: 1 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Service</TableCell>
                <TableCell>Schedule</TableCell>
                <TableCell>Time</TableCell>
                <TableCell align="right">Base price</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.map((rule) => (
                <TableRow key={rule.rule_id}>
                  <TableCell>{formatFacilityService(rule.service)}</TableCell>
                  <TableCell>{formatRuleRepeat(rule)}</TableCell>
                  <TableCell>{formatRuleTime(rule)}</TableCell>
                  <TableCell align="right">
                    {formatMoney(rule.base_price, rule.currency)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
